/**
 * NPC v3 — Premium homepage redesign (preview-only route).
 *
 * Mounted at `/v3` so the current `/` Home stays untouched. Inspired by the layout
 * cadence and premium feel of superhealth.co.in/vip but built original.
 * Content from `/app/memory/PRD.md` artefact NPPC-HOMEPAGE.docx.
 *
 * One-file deliverable to keep the scope tight and the credit cost low.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Phone, ShieldCheck, HeartPulse, Stethoscope, Microscope, Pill,
  BadgePercent, Calendar, Users, Sparkles, Check, MapPin, Award,
  Activity, Baby, Bone, Brain, Eye, Smile, Mail, ChevronDown,
} from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_elite-health-members/artifacts/mjaynxjr_WhatsApp%20Image%202026-05-19%20at%2012.18.27%20PM.jpeg";
const LOGO     = "https://customer-assets.emergentagent.com/job_elite-health-members/artifacts/qwyjgnc0_Nirmal%20Hospital%20Mulit%20R%20Logo.png";

/* ---------- Mini design tokens ---------- */
const c = {
  ink: "#0A1929",
  inkSoft: "#1B3253",
  mute: "#5C6B7A",
  paper: "#FAFBFC",
  card: "#FFFFFF",
  bronze: "#B8826E",
  silver: "#8A98A4",
  gold: "#C89B3C",
  red: "#A53A2A",
  navy: "#1E4F87",
  green: "#1F7A3D",
};

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

/* ---------- Page ---------- */
export default function V3Home() {
  return (
    <div className="min-h-screen bg-[var(--paper)]" style={{ ["--paper"]: c.paper, ["--ink"]: c.ink }}>
      <V3Header />
      <Hero />
      <Marquee />
      <DifferenceSection />
      <EcosystemSection />
      <BenefitsSection />
      <PlansPreview />
      <SpecialitiesSection />
      <WellnessSection />
      <Testimonials />
      <FinalCTA />
      <V3Footer />
    </div>
  );
}

/* ───────────────────────── HEADER ───────────────────────── */
function V3Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 12);
    f(); window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  const navItems = [
    ["#difference", "About"],
    ["#ecosystem", "Ecosystem"],
    ["#benefits", "Benefits"],
    ["#plans", "Plans"],
    ["#specialities", "Doctors"],
  ];
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_8px_30px_-12px_rgba(15,37,64,0.18)]" : "bg-gradient-to-b from-black/30 to-transparent"}`} data-testid="v3-header">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 h-[68px] md:h-[80px] flex items-center justify-between">
        <Link to="/v3" className="flex items-center gap-3">
          <img src={LOGO} alt="Nirmal Hospital" className="h-10 md:h-12 w-auto" draggable={false} />
          <div className={`hidden md:flex flex-col leading-tight border-l pl-3 ${scrolled ? "border-[#E1E8F0]" : "border-white/30"}`}>
            <span className={`text-[11px] uppercase tracking-[0.22em] font-bold ${scrolled ? "text-[#A53A2A]" : "text-[#F5C6BE]"}`}>Privilege Card</span>
            <span className={`text-[10px] tracking-wide italic ${scrolled ? "text-[#5C6B7A]" : "text-white/80"}`}>Care par excellence</span>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map(([href, label]) => (
            <a key={href} href={href}
              className={`group relative px-4 py-2 rounded-full text-[15px] font-semibold transition-colors ${scrolled ? "text-[#0F2540] hover:text-[#1E4F87]" : "text-white/90 hover:text-white"}`}>
              <span className="relative">
                {label}
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 group-hover:w-5 h-[2px] rounded-full bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] transition-all duration-300" />
              </span>
            </a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:+916357033301" className={`text-sm font-semibold inline-flex items-center gap-1.5 ${scrolled ? "text-[#0F2540]" : "text-white"}`}>
            <Phone className="h-4 w-4" /> +91 63570 33301
          </a>
          <Link to="/join" className="ml-2 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(165,58,42,0.35)] transition-all duration-300" data-testid="v3-cta-join">
            Join NPC <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <button onClick={() => setOpen(!open)} className={`lg:hidden h-10 w-10 grid place-items-center rounded-full border transition-colors ${scrolled ? "border-[#E1E8F0] text-[#0F2540]" : "border-white/30 text-white"}`} aria-label="Menu">
          <span className={`block w-5 h-0.5 transition-transform ${scrolled ? "bg-[#0F2540]" : "bg-white"} ${open ? "rotate-45 translate-y-[3px]" : ""}`} />
          <span className={`block w-5 h-0.5 mt-1 transition-transform ${scrolled ? "bg-[#0F2540]" : "bg-white"} ${open ? "-rotate-45 -translate-y-[1px]" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E1E8F0] shadow-lg">
          <div className="px-5 py-3 flex flex-col gap-1">
            {navItems.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-base font-semibold text-[#0F2540] hover:bg-[#F4F7FB]">{label}</a>
            ))}
            <Link to="/join" onClick={() => setOpen(false)} className="mt-2 px-4 py-3 rounded-full text-center text-sm font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C]">Join NPC</Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ───────────────────────── HERO ───────────────────────── */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return (
    <section ref={ref} className="relative h-[100vh] min-h-[680px] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Nirmal Multi Speciality Hospital" className="w-full h-[120%] object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1929]/85 via-[#0A1929]/55 to-[#0A1929]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(200,155,60,0.18),transparent_50%)]" />
      </motion.div>
      <motion.div style={{ opacity }} className="relative h-full flex items-center">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white pt-16">
            <motion.div {...fadeUp}>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/8 backdrop-blur border border-[#C89B3C]/30 text-xs uppercase tracking-[0.22em] text-[#F5C6BE] font-bold">
                <ShieldCheck className="h-3.5 w-3.5 text-[#C89B3C]" />
                Nirmal Privilege Card · Care par excellence
              </span>
            </motion.div>
            <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="mt-7 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-bold leading-[0.98] tracking-[-0.02em]">
              A hospital
              <br /><span className="text-[#F5C6BE]">with</span> a
              <br /><span className="bg-gradient-to-r from-[#E3BC58] via-[#C89B3C] to-[#9C7421] bg-clip-text text-transparent">difference.</span>
            </motion.h1>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.25 }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-white/85">
              Healthcare built on compassion, trust, transparency and timely care — a long-term healthcare relationship that supports your family at every stage of life.
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-3">
              <Link to="/join" className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(200,155,60,0.45)] transition-all duration-300" data-testid="v3-hero-cta-join">
                Join NPC today <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#difference" className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-semibold text-white border border-white/30 hover:bg-white/10 transition-all duration-300">
                Discover more <ChevronDown className="h-4 w-4" />
              </a>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.55 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[["30+", "Specialists"], ["24×7", "Helpline"], ["12 mo", "Validity"]].map(([k, v]) => (
                <div key={k}>
                  <div className="font-display text-3xl font-bold text-white">{k}</div>
                  <div className="text-[11px] uppercase tracking-wider text-white/60 font-semibold mt-0.5">{v}</div>
                </div>
              ))}
            </motion.div>
          </div>
          {/* Floating ID card */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }}
            className="lg:col-span-5 hidden lg:block">
            <div className="relative ml-auto max-w-sm">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                className="relative rounded-3xl bg-gradient-to-br from-[#0F2540] via-[#1E4F87] to-[#0F2540] p-8 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.45)] ring-1 ring-white/10"
              >
                <div className="absolute -top-2 -right-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#E3BC58] to-[#9C7421] text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">Gold · Family</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#F5C6BE] font-semibold">Nirmal Privilege Card</div>
                <div className="mt-1 font-display text-3xl font-bold text-white">PRIVILEGE</div>
                <div className="text-[11px] text-white/70 italic mt-0.5">Care par excellence</div>
                <div className="mt-12 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Member ID</div>
                    <div className="font-mono text-lg text-white">NPC 0019 62</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Valid till</div>
                    <div className="font-mono text-sm text-white">12/2026</div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-1.5">
                  {[...Array(36)].map((_, i) => <span key={i} className="h-1 rounded bg-white/8" />)}
                </div>
              </motion.div>
              {/* Floating accent tile */}
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -left-8 -bottom-6 bg-white rounded-2xl p-4 shadow-xl ring-1 ring-black/5 flex items-center gap-3"
              >
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#1F7A3D] to-[#155F2D] grid place-items-center text-white">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C6B7A] font-bold">Verified</div>
                  <div className="text-sm font-bold text-[#0F2540]">2,400+ members</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-[10px] uppercase tracking-[0.3em] flex items-center gap-2 animate-pulse">
        <span>Scroll</span>
        <ChevronDown className="h-3 w-3" />
      </div>
    </section>
  );
}

/* ───────────────────────── MARQUEE ───────────────────────── */
function Marquee() {
  const items = ["Cardiology", "Orthopedics", "Gynecology", "Pediatrics", "General Surgery", "Internal Medicine", "ENT", "Ophthalmology", "Neurology"];
  const loop = [...items, ...items, ...items];
  return (
    <section className="border-y border-[#E1E8F0] bg-white overflow-hidden" data-testid="v3-marquee">
      <div className="py-5">
        <div className="flex items-center gap-12 whitespace-nowrap animate-[marquee_38s_linear_infinite]" style={{ width: "max-content" }}>
          {loop.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-[#0F2540]">
              <Sparkles className="h-3.5 w-3.5 text-[#C89B3C]" /> {s}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── A HOSPITAL WITH A DIFFERENCE ───────────────────────── */
function DifferenceSection() {
  return (
    <section id="difference" className="py-24 lg:py-32 bg-[#FAFBFC]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div {...fadeUp} className="lg:col-span-6">
          <span className="inline-block text-[11px] uppercase tracking-[0.28em] text-[#A53A2A] font-bold">Our promise</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2540] leading-[1.05]">
            A hospital with <br />a <span className="text-[#A53A2A]">difference.</span>
          </h2>
          <p className="mt-7 text-lg text-[#5C6B7A] leading-relaxed max-w-xl">
            At Nirmal Super Speciality Hospital, healthcare goes beyond infrastructure and technology — it is built on compassion, trust, transparency, and timely medical care.
          </p>
          <p className="mt-4 text-lg text-[#5C6B7A] leading-relaxed max-w-xl">
            Our goal is to create long-term healthcare relationships that support individuals and families at every stage of life.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            {[
              [ShieldCheck, "Trust", "Transparent care"],
              [HeartPulse,  "Compassion", "Family-first ethos"],
              [Activity,    "Timely care", "24×7 helpline"],
              [Award,       "Excellence", "Proven outcomes"],
            ].map(([Icon, t, d]) => (
              <div key={t} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shadow-sm ring-1 ring-[#E1E8F0] text-[#A53A2A] flex-shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#0F2540]">{t}</div>
                  <div className="text-xs text-[#5C6B7A]">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="lg:col-span-6 relative">
          <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#E1E8F0] shadow-2xl">
            <img src={HERO_IMG} alt="Hospital" className="w-full h-[540px] object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0F2540]/85 via-[#0F2540]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#F5C6BE] font-bold mb-1">Nirmal Super Speciality Hospital</div>
              <div className="font-display text-2xl font-bold">Adajan · L. P. Savani · Surat</div>
            </div>
          </div>
          <div className="absolute -top-5 -right-5 bg-white rounded-2xl p-5 shadow-xl ring-1 ring-black/5 hidden md:block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B7A] font-bold">Recognised by</div>
            <div className="mt-1 font-display text-lg font-bold text-[#0F2540]">NABH-aligned</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ───────────────────────── ECOSYSTEM ───────────────────────── */
function EcosystemSection() {
  const pillars = [
    [Stethoscope, "Specialist OPD", "Unlimited consultations with eligible doctors during regular OPD hours."],
    [Microscope,  "Diagnostics",    "Privileged rates on pathology, radiology and preventive health packages."],
    [Pill,        "Pharmacy",       "In-house pharmacy benefits across eligible medications."],
    [BadgePercent, "Hospital billing","Privileged rates on eligible hospital services."],
    [Calendar,    "Preventive care","Guidance, follow-ups and continuous health support."],
    [Users,       "Wellness",       "Awareness programs and family wellness initiatives."],
  ];
  return (
    <section id="ecosystem" className="relative py-24 lg:py-32 bg-[#0A1929] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[length:22px_22px] pointer-events-none" />
      <div className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-[#1E4F87]/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#A53A2A]/20 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#F5C6BE] font-bold">Your family's</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
            Healthcare ecosystem.
          </h2>
          <p className="mt-6 text-lg text-white/75 max-w-2xl">
            Nirmal Privilege Card is more than a discount card — it is a comprehensive healthcare support ecosystem that keeps your family connected with trusted medical care throughout the year.
          </p>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map(([Icon, t, d], i) => (
            <motion.div key={t} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.06 }}
              className="group relative rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/10 p-7 hover:bg-white/[0.06] hover:border-[#C89B3C]/35 transition-all duration-500">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#A53A2A] to-[#7A2A1E] grid place-items-center shadow-[0_10px_30px_-10px_rgba(165,58,42,0.7)] group-hover:scale-110 transition-transform duration-500">
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold tracking-tight">{t}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── BENEFITS ───────────────────────── */
function BenefitsSection() {
  const benefits = [
    "Unlimited OPD consultations with eligible specialists",
    "Exclusive healthcare privileges",
    "Special benefits on eligible hospital services",
    "Preventive healthcare support and guidance",
    "Access to wellness initiatives and awareness programs",
  ];
  const points = [
    "Consult eligible doctors multiple times during OPD hours",
    "Smarter access to healthcare services for your family",
    "Continuous healthcare support under one program",
    "Compassionate healthcare with a difference",
  ];
  return (
    <section id="benefits" className="py-24 lg:py-32 bg-[#FAFBFC]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#A53A2A] font-bold">Key benefits</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2540] leading-[1.05]">
            Smart healthcare<br />privilege program for <br /><span className="text-[#A53A2A]">your family.</span>
          </h2>
          <p className="mt-6 text-lg text-[#5C6B7A] max-w-2xl">
            Nirmal Privilege Card is thoughtfully designed to make quality healthcare more accessible, affordable, and family-focused — all under one smart, value-driven program.
          </p>
        </motion.div>
        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <motion.div {...fadeUp} className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {benefits.map((b, i) => (
              <motion.div key={b} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                className="group relative rounded-2xl bg-white p-6 ring-1 ring-[#E1E8F0] hover:ring-[#C89B3C]/40 hover:shadow-[0_20px_40px_-20px_rgba(165,58,42,0.25)] hover:-translate-y-1 transition-all duration-500">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#A53A2A] to-[#C89B3C] grid place-items-center text-white mb-4 group-hover:rotate-6 transition-transform duration-500">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <p className="text-[#0F2540] font-semibold leading-relaxed">{b}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="lg:col-span-5">
            <div className="relative rounded-3xl bg-gradient-to-br from-[#0F2540] via-[#1E4F87] to-[#0F2540] p-9 text-white h-full overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#C89B3C]/20 blur-2xl" />
              <Sparkles className="h-6 w-6 text-[#C89B3C]" />
              <h3 className="mt-4 font-display text-3xl font-bold leading-tight">Simple. Affordable. Valuable.</h3>
              <ul className="mt-7 space-y-3">
                {points.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C89B3C] flex-shrink-0" />
                    <span className="text-sm text-white/90 leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-white/15 flex items-center gap-3 text-xs">
                <MapPin className="h-4 w-4 text-[#F5C6BE]" />
                <span className="text-white/80">Available exclusively at <b className="text-white">Nirmal Super Speciality Hospital</b></span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── PLANS ───────────────────────── */
function PlansPreview() {
  const plans = [
    { tier: "Bronze",  name: "Individual",     price: "₹2,000", grad: "from-[#C28B6F] via-[#A8704F] to-[#8B5A3C]", points: ["1 member · 12 months", "Unlimited OPD", "20% on diagnostics"] },
    { tier: "Silver",  name: "Husband Wife",   price: "₹3,000", grad: "from-[#B6C2CC] via-[#8A98A4] to-[#5E6B78]", points: ["2 members · 12 months", "Unlimited OPD", "20% on diagnostics"], popular: true },
    { tier: "Gold",    name: "Family",         price: "₹6,000", grad: "from-[#E3BC58] via-[#C89B3C] to-[#9C7421]", points: ["Up to 6 members", "Unlimited OPD", "25% on diagnostics + 15% pharmacy"] },
  ];
  return (
    <section id="plans" className="py-24 lg:py-32 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#A53A2A] font-bold">Choose your tier</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2540] leading-[1.05]">
              Three tiers.<br />One promise.
            </h2>
          </div>
          <Link to="/plans" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#1E4F87] hover:text-[#0F2540] transition-colors">
            Compare all plans <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {plans.map((p, i) => (
            <motion.div key={p.tier} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.08 }}
              className={`group relative rounded-3xl bg-white ring-1 ring-[#E1E8F0] overflow-hidden hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(15,37,64,0.25)] transition-all duration-500 ${p.popular ? "ring-2 ring-[#C89B3C]/45" : ""}`}>
              <div className={`h-2 bg-gradient-to-r ${p.grad}`} />
              {p.popular && <div className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-[#A53A2A] to-[#7A2A1E] text-[9px] font-bold uppercase tracking-wider text-white shadow-lg">Most loved</div>}
              <div className="p-8">
                <div className={`inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold text-white bg-gradient-to-r ${p.grad}`}>{p.tier} · {p.name}</div>
                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="font-display text-5xl font-bold text-[#0F2540]">{p.price}</span>
                  <span className="text-sm text-[#5C6B7A]">/year</span>
                </div>
                <p className="mt-1 text-xs text-[#5C6B7A]">Price includes 18% GST</p>
                <div className="my-6 h-px bg-gradient-to-r from-transparent via-[#E1E8F0] to-transparent" />
                <ul className="space-y-2.5 text-sm text-[#0F2540]">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" /> {pt}</li>
                  ))}
                </ul>
                <Link to="/join" className="mt-8 inline-flex w-full justify-center items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#0F2540] to-[#1E4F87] hover:from-[#A53A2A] hover:to-[#C89B3C] transition-colors duration-500">
                  Choose {p.tier} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── SPECIALITIES / DOCTORS ───────────────────────── */
function SpecialitiesSection() {
  const specs = [
    [HeartPulse,    "Cardiology",         "Heart specialists"],
    [Bone,          "Orthopedics",        "Joint & bone care"],
    [Baby,          "Pediatrics",         "Child specialists"],
    [Brain,         "Neurology",          "Brain & nerve"],
    [Stethoscope,   "Internal Medicine",  "Adult medicine"],
    [Microscope,    "Diagnostics",        "Lab + radiology"],
    [Eye,           "Ophthalmology",      "Eye care"],
    [Smile,         "Dental",             "Smile design"],
  ];
  return (
    <section id="specialities" className="py-24 lg:py-32 bg-[#FAFBFC]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#A53A2A] font-bold">Specialities covered</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2540] leading-[1.05]">
            Care across<br />every <span className="text-[#A53A2A]">life stage.</span>
          </h2>
          <p className="mt-6 text-lg text-[#5C6B7A] max-w-2xl">
            Department visuals and doctor panels — 30+ specialists across the disciplines that matter most to your family.
          </p>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {specs.map(([Icon, t, d], i) => (
            <motion.div key={t} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 * i }}
              className="group relative rounded-2xl bg-white p-6 ring-1 ring-[#E1E8F0] hover:-translate-y-1 hover:shadow-[0_24px_40px_-24px_rgba(165,58,42,0.3)] hover:ring-[#A53A2A]/30 transition-all duration-500 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#1E4F87] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#F5C6BE] to-[#FAFBFC] grid place-items-center text-[#A53A2A] ring-1 ring-[#A53A2A]/15 group-hover:from-[#A53A2A] group-hover:to-[#7A2A1E] group-hover:text-white transition-all duration-500">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-display text-base font-semibold text-[#0F2540]">{t}</div>
              <div className="text-xs text-[#5C6B7A] mt-1">{d}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── WELLNESS ───────────────────────── */
function WellnessSection() {
  return (
    <section className="py-24 lg:py-32 bg-gradient-to-br from-[#FCEDE7] via-[#FAFBFC] to-[#EAF1FA]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div {...fadeUp} className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#A53A2A]/20 to-[#C89B3C]/20 rounded-[2.5rem] blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/40 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=900&q=70" alt="Family wellness" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2540]/40 via-transparent to-transparent" />
            </div>
          </div>
        </motion.div>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="lg:col-span-7 order-1 lg:order-2">
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#A53A2A] font-bold">Family wellness</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2540] leading-[1.05]">
            Healthcare designed<br />for <span className="text-[#A53A2A]">modern families.</span>
          </h2>
          <p className="mt-6 text-lg text-[#5C6B7A] max-w-xl leading-relaxed">
            From a child's first vaccination to a parent's annual health check, NPC ties together every healthcare moment under one trusted, compassionate program.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
            {[
              ["Preventive care", "Annual health-check support and proactive screening guidance."],
              ["Continuous support", "Long-term care relationships — one card, one trusted team."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-2xl bg-white/70 backdrop-blur p-5 ring-1 ring-white/60 shadow-sm">
                <div className="font-display text-base font-semibold text-[#0F2540]">{t}</div>
                <p className="text-sm text-[#5C6B7A] mt-1 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ───────────────────────── TESTIMONIALS ───────────────────────── */
function Testimonials() {
  const quotes = [
    { q: "The Privilege Card made looking after my parents so much simpler. The team feels like family.", a: "Hetal P.", r: "Family Gold member" },
    { q: "Unlimited OPD and quick access to specialists — exactly what a working couple needs.", a: "Amit & Rina", r: "Husband Wife Silver" },
    { q: "Transparent, compassionate, and genuinely caring. A hospital with a difference, truly.", a: "Dr. K. Mehta", r: "Patient since 2021" },
  ];
  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp}>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#A53A2A] font-bold">Member stories</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540] leading-tight">Trust, in their words.</h2>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {quotes.map((q, i) => (
            <motion.figure key={q.a} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-2xl bg-[#FAFBFC] p-8 ring-1 ring-[#E1E8F0] hover:ring-[#C89B3C]/35 transition-all duration-500">
              <div className="text-5xl text-[#C89B3C] font-serif leading-none -mb-2">“</div>
              <blockquote className="text-[15px] text-[#0F2540] leading-relaxed">{q.q}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#A53A2A] to-[#C89B3C] text-white grid place-items-center text-sm font-bold">{q.a[0]}</div>
                <div>
                  <div className="text-sm font-semibold text-[#0F2540]">{q.a}</div>
                  <div className="text-[11px] text-[#5C6B7A]">{q.r}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── FINAL CTA ───────────────────────── */
function FinalCTA() {
  return (
    <section id="join" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1929] via-[#1B3253] to-[#0A1929]" />
      <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[length:22px_22px] pointer-events-none" />
      <div className="absolute -top-32 left-1/4 w-[420px] h-[420px] rounded-full bg-[#A53A2A]/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 right-1/4 w-[420px] h-[420px] rounded-full bg-[#C89B3C]/20 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-5xl px-6 lg:px-10 text-center">
        <motion.div {...fadeUp}>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#F5C6BE] font-bold">Care par excellence</span>
          <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.02]">
            Join <span className="bg-gradient-to-r from-[#F5C6BE] via-[#C89B3C] to-[#F5C6BE] bg-clip-text text-transparent">NPC</span> today.
          </h2>
          <p className="mt-6 text-lg text-white/80 max-w-2xl mx-auto">
            Healthcare support designed for modern families. Start your privileged journey with Nirmal Hospital.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/join" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(200,155,60,0.55)] transition-all duration-300" data-testid="v3-final-cta">
              Join NPC today <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:+916357033301" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-white border border-white/30 hover:bg-white/10 transition-all duration-300">
              <Phone className="h-4 w-4" /> Talk to advisor
            </a>
          </div>
          <p className="mt-8 text-xs text-white/50 uppercase tracking-wider">
            Available exclusively at Nirmal Super Speciality Hospital · Adajan · Surat
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ───────────────────────── FOOTER ───────────────────────── */
function V3Footer() {
  return (
    <footer className="bg-[#08111E] text-white border-t border-white/8">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="bg-white inline-block rounded-md px-3 py-2">
            <img src={LOGO} alt="Nirmal" className="h-9 w-auto" />
          </div>
          <p className="mt-4 text-sm text-white/65 max-w-md leading-relaxed">
            Nirmal Multi Speciality Hospital · Privilege Card — premium healthcare membership for modern families. Care par excellence.
          </p>
          <div className="mt-5 flex items-center gap-2 text-sm text-white/75">
            <MapPin className="h-4 w-4 text-[#F5C6BE]" /> Adajan, L. P. Savani, Surat — Gujarat 395009
          </div>
        </div>
        <div>
          <h4 className="font-display text-sm font-semibold tracking-wide text-white">Reach us</h4>
          <span className="block mt-1.5 h-[2px] w-8 rounded-full bg-gradient-to-r from-[#A53A2A] to-[#C89B3C]" />
          <ul className="mt-5 space-y-2.5 text-sm text-white/75">
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#F5C6BE]" /> <a href="tel:+916357033301" className="hover:text-white">+91-63570-33301</a></li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#F5C6BE]" /> <a href="mailto:npc@nirmalhospitals.in" className="hover:text-white">npc@nirmalhospitals.in</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display text-sm font-semibold tracking-wide text-white">Quick</h4>
          <span className="block mt-1.5 h-[2px] w-8 rounded-full bg-gradient-to-r from-[#1E4F87] to-[#C89B3C]" />
          <ul className="mt-5 space-y-2.5 text-sm text-white/75">
            <li><Link to="/plans" className="hover:text-white">Plans</Link></li>
            <li><Link to="/benefits" className="hover:text-white">Benefits</Link></li>
            <li><Link to="/join" className="hover:text-white">Join NPC</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/8">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 text-xs text-white/55 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Nirmal Hospital Pvt. Ltd. · All rights reserved.</span>
          <span>v3 redesign · preview only</span>
        </div>
      </div>
    </footer>
  );
}
